import MasterclassCard from "@/components/Masterclass/MasterclassCard";
import Layout from "@/components/layout";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import { cn } from "@/utils/cn";
import { NextPageContext } from "next";
import { Tag } from "../teachers";
import webengage from "@/webengage";
import mixpanel from "@/utils/mixpanel";
import Head from "next/head";
import Breadcrumb from "@/components/BreadCrumb";

export async function getServerSideProps(ctx: NextPageContext) {
  try {
    const subCategory = ctx.query.category as string;
    const { data: fetchedSubCategories } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}categories/sub-categories/${financeId}`,
      {
        headers: { "Accept-Encoding": "gzip,deflate,compress" },
      }
    );

    let masterclassUrl = `${process.env.NEXT_PUBLIC_BASE_API}masterclasses?select=slots&getFutureSlots=true&category=Finance`;
    if (subCategory && typeof subCategory === "string") {
      const subCategoryId = fetchedSubCategories.data.find(
        (tag: any) => tag.slug === subCategory
      )?.id;

      if (subCategoryId) {
        masterclassUrl = `${process.env.NEXT_PUBLIC_BASE_API}masterclasses/masterclass-by-subcategory/${subCategoryId}?getFutureSlots=true`;
      }
    }

    const fetchedMasterclasses = await axios.get(masterclassUrl, {
      headers: { "Accept-Encoding": "gzip,deflate,compress" },
    });

    return {
      props: {
        masterclasses: fetchedMasterclasses.data,
        tags: fetchedSubCategories.data,
      },
    };
  } catch (err) {
    console.log(err);
    return {
      props: {
        masterclasses: [],
        tags: [],
      },
    };
  }
}

export const financeId = "clcj0zbvt000kgr0qr873028e";

export default function MasterClasses({
  masterclasses,
  tags,
}: {
  masterclasses: any;
  tags: any;
}) {
  const router = useRouter();
  const [activeCategory, setActiveCategory] = useState<{
    name: string;
    id: string;
    slug: string;
  } | null>(null);

  const tagsArray = tags || [];
  const TAGS: Tag = {};
  tagsArray.forEach((tag: any) => {
    TAGS[tag.slug] = {
      name: tag?.name,
      id: tag?.id,
      slug: tag?.slug,
    };
  });

  useEffect(() => {
    setActiveCategory(
      router.query.category ? TAGS[router.query.category.toString()] : null
    );
  }, [router.query.category]);

  useEffect(() => {
    const _webengage = webengage();
    if (_webengage)
      _webengage.track("Masterclass Page viewed", {
        source: "Tradewise",
      });
  }, []);

  return (
    <>
      <Head>
        <title>
          Free Trading Courses | Learn Expert Trading Strategies with Tradewise
        </title>
        <meta
          name="description"
          content="Access free trading courses at Tradewise. Start your trading journey with expert strategies, market insights, and valuable resources—all for free!"
        />
      </Head>
      <Layout>
        <Breadcrumb classNames="mb-1" />
        <div className="flex flex-col gap-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ease: "linear", duration: 0.25 }}
            className="flex flex-col gap-4 md:flex-row md:justify-between"
          >
            <div className="flex flex-row max-md:gap-4 justify-start items-center">
              <ArrowLeftIcon
                className="w-4 h-4 hover:cursor-pointer md:hidden"
                onClick={() => router.back()}
              />
              <h3 className="text-[#161C2B] max-lg:text-xl font-bold">
                Upcoming Free Classes
              </h3>
            </div>
            <div className="max-md:w-full overflow-x-auto hide_scrollbar max-md:flex max-md:gap-1 max-md:justify-evenly max-md:items-center md:space-x-4">
              <button
                onClick={() =>
                  router.push(
                    `/masterclasses${
                      router?.query?.referer
                        ? `?referer=${router?.query?.referer}`
                        : ""
                    }${
                      router?.query?.comment
                        ? `&comment=${router?.query?.comment}`
                        : ""
                    }`
                  )
                }
                className={cn(
                  "rounded-full whitespace-nowrap text-sm md:text-[15px] px-3 md:px-3.5 py-1 md:py-1.5 font-medium capitalize border-none",

                  !activeCategory
                    ? "bg-foreground !text-white hover:text-white"
                    : "bg-primary/10 text-foreground"
                )}
              >
                All
              </button>
              {tagsArray
                .filter((tag: any) => tag.name !== "Forex")
                .map((tag: any) => (
                <button
                  onClick={() => {
                    const _webengage = webengage();
                    if (_webengage) {
                      _webengage.track("Category Clicked", {
                        industry: tag.name,
                        source: "Tradewise",
                      });
                    }
                    router.push(
                      `/masterclasses?category=${tag.slug}${
                        router?.query?.referer
                          ? `&referer=${router?.query?.referer}`
                          : ""
                      }${
                        router?.query?.comment
                          ? `&comment=${router?.query?.comment}`
                          : ""
                      }`
                    );
                    mixpanel.track("explore_category", {
                      page_location: "Free Masterclasses",
                      page_referrer: document.referrer,
                      location: "tabs",
                      subcategory: activeCategory?.name,
                    });
                  }}
                  className={cn(
                    "rounded-full text-sm md:text-[15px] px-3 md:px-3.5 py-1 md:py-1.5 font-medium capitalize border-none whitespace-nowrap",
                    tag?.name === activeCategory?.name
                      ? "bg-foreground !text-white hover:text-white"
                      : "bg-primary/10 text-foreground"
                  )}
                  key={tag?.id}
                >
                  {tag?.name}
                </button>
              ))}
            </div>
          </motion.div>
          <div className="py-2 gap-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 place-items-center place-content-center">
            {masterclasses?.data?.map((masterclass: any, index: number) => {
              return (
                <MasterclassCard
                  initial={{ opacity: 0, translateY: 40 }}
                  animate={{ opacity: 1, translateY: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: 0.2 * index,
                    type: "tween",
                  }}
                  key={masterclass?.id}
                  masterclass={masterclass}
                  referer={router?.query?.referer}
                  comment={router?.query?.comment}
                />
              );
            })}
          </div>
        </div>
      </Layout>
    </>
  );
}
